import { CloseIcon, InfoOutlineIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box, Flex, Input, InputGroup, InputLeftElement, InputRightElement, List, ListItem, Spinner, Switch, Text, Tooltip, useToast,
  Modal, ModalBody, ModalCloseButton, ModalHeader, ModalContent, ModalFooter, Button, ModalOverlay, useColorModeValue,
  SkeletonText
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useGetTargetCustomAudienceQuery } from 'store/facebookServices/facebookServicesApi';
import { useGetPixelsDetailsByPixelIdQuery } from 'store/BmAccounts/bmAccountApi';
import { useCreateBotNullifyMutation, useGetBotNullifyListQuery } from 'store/campaigns/adsetFormApi';

const shouldRemoveCustomeAudience = (newAudience, existingAudience) => {
  return newAudience.audience_id === existingAudience.audience_id;
};

const filterSelectedCustomAudiences = (newAudience, selectedCustomAudiences) => {
  return selectedCustomAudiences.filter((audience) => !shouldRemoveCustomeAudience(newAudience, audience));
};

const IncludeCustomAudienceWithBotNullifierUpdate = ({ adsAccount, promoted_object, onIncludeAudienceChange, preFilledTargets, context }) => {
  const inputBackground = useColorModeValue("#f8f6fb", "#1e1530");
  const borderColor = useColorModeValue('gray.300', 'gray.500');
  const hoverColor = useColorModeValue('#f0f0f0', 'gray.700');

  const toast = useToast();
  const inputRef = useRef(null);
  const popupRef = useRef(null);
  const hasCreatedAudienceRef = useRef(false);
  const initializedFromUpdateRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [customAudienceOptions, setCustomAudienceOptions] = useState([]);
  const [selectedCustomAudiences, setselectedCustomAudiences] = useState(
    preFilledTargets?.length > 0
      ? preFilledTargets.map(target => ({
        audience_id: target?.id,
        name: target?.name
      }))
      : []
  );

  const [showPopup, setShowPopup] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { adsAccountId, adsAccountName } = adsAccount || {};
  const [isChecked, setIsChecked] = useState(
    context === 'update' ? false : (preFilledTargets?.length > 0)
  );

  const [isBotListLoading, setIsBotListLoading] = useState(false);
  const [isSwitchDisabled, setIsSwitchDisabled] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorUrl, setErrorUrl] = useState(null);
  const [shouldFetchBotNullifyList, setShouldFetchBotNullifyList] = useState(false);

  let matchedPixel;
  if (context === 'update') {
    matchedPixel = promoted_object;
  } else {
    matchedPixel = Array.isArray(promoted_object)
      ? promoted_object.find(item => item?.adsAccountId === adsAccountId)
      : undefined;
  }

  const [getCustomAudience] = useCreateBotNullifyMutation();

  const { data: BotNullifyList, error: isBotListtError, isFetching: isBotListFetching, refetch: refetchBotList } = useGetBotNullifyListQuery(
    { adsAccountId },
    { skip: !shouldFetchBotNullifyList }
  );

  const { data: PixleDetails, isLoading: isPixelDetailsLoading } = useGetPixelsDetailsByPixelIdQuery(
    { pixelId: matchedPixel?.pixel_id },
    { skip: !matchedPixel?.pixel_id }
  );

  const { data: customAudiences, isLoading, isFetching, refetch } = useGetTargetCustomAudienceQuery(
    { searchTerm: debouncedSearchTerm, adsAccountId },
    { skip: !isInputFocused }
  );

  // For update context: fetch bot list immediately
    useEffect(() => {
        if (context === 'update' && !initializedFromUpdateRef.current) {
        setShouldFetchBotNullifyList(true);
        }
    }, [context]);

    // After BotNullifyList loads in update mode, set isChecked only if there's an ID overlap
    useEffect(() => {
        if (context !== 'update') return;
        if (initializedFromUpdateRef.current) return;
        if (!Array.isArray(BotNullifyList)) return; // wait until it’s an array (loaded or empty)
    
        const prefilledIds = new Set((preFilledTargets || []).map(t => t?.id));
        const hasMatch = BotNullifyList.some(item => prefilledIds.has(item?.botId));
        setIsChecked(hasMatch);
        initializedFromUpdateRef.current = true;
    }, [context, BotNullifyList, preFilledTargets]);

  // If switch is off and a bot list exists, remove those entries from selection
  // BUT do not remove while the modal is open.
  useEffect(() => {
    if (!isChecked && BotNullifyList?.length > 0 && !isModalOpen) {
      const botAudienceIds = BotNullifyList.map(item => item.botId);
      const filteredAudiences = selectedCustomAudiences.filter(
        audience => !botAudienceIds.includes(audience.audience_id)
      );
      setselectedCustomAudiences(filteredAudiences);
      onIncludeAudienceChange(filteredAudiences);
    }
  }, [isChecked, BotNullifyList, isModalOpen]);

  // When turning on and bot list exists, add them (avoid duplicates)
  useEffect(() => {
    if (isChecked && BotNullifyList?.length > 0 && !(preFilledTargets?.length > 0)) {
      const botAudiences = BotNullifyList.map(item => ({
        audience_id: item.botId,
        name: item.botName
      }));

      setselectedCustomAudiences(prev => {
        const updated = [
          ...prev.filter(a => !botAudiences.some(b => b.audience_id === a.audience_id)),
          ...botAudiences
        ];
        setTimeout(() => onIncludeAudienceChange(updated), 0);
        return updated;
      });
    }
  }, [isChecked, BotNullifyList]);

  useEffect(() => {
    if (isChecked && !BotNullifyList) {
      setIsBotListLoading(true);
    } else if (BotNullifyList?.length > 0) {
      setIsBotListLoading(false);
    }
  }, [isChecked, BotNullifyList]);

  // Enable switch as soon as pixel details show up
  useEffect(() => {
    if (PixleDetails && !hasCreatedAudienceRef.current) {
        setIsSwitchDisabled(false);
    }
  }, [PixleDetails]);

  // Create/ensure bot audience on toggle ON
  useEffect(() => {
    if (!PixleDetails) return;

    if (isChecked) {
      // Ensure we try to fetch any existing bot list first
      if (!shouldFetchBotNullifyList) setShouldFetchBotNullifyList(true);

      getCustomAudience({
        payload: {
          pixelId: PixleDetails?.id,
          pixelName: PixleDetails?.name || '',
          adsAccount: {
            adsAccountName,
            adsAccountId,
          },
        },
      })
        .unwrap()
        .then(() => {
          hasCreatedAudienceRef.current = true;
          setShouldFetchBotNullifyList(true);
          refetchBotList?.();
        })
        .catch((error) => {
          const errorMessage = error?.data?.message || 'An error occurred';
          const urlMatch = errorMessage.match?.(/https?:\/\/[^\s]+/);
          const hasPreviousList = Array.isArray(BotNullifyList) && BotNullifyList.length > 0;

          // Always keep the switch enabled if there is a previous list we can fall back to
          setIsSwitchDisabled(false);
          setShouldFetchBotNullifyList(true);
          refetchBotList?.();

          if (urlMatch && !hasPreviousList) {
            // No previous list: block flow and push user to accept TOS
            setErrorUrl(urlMatch[0]);
            setIsModalOpen(true);
            setIsChecked(false);
          } else {
            toast({
              position: 'top-right',
              title: 'Error',
              description: errorMessage,
              status: 'error',
              duration: 4000,
              isClosable: true,
            });
          }
        });
    }
  }, [isChecked, PixleDetails, adsAccountId]); 

  //If after fetching the list it's empty, turn switch off, disable it, and inform user
  useEffect(() => {
    if (!shouldFetchBotNullifyList) return;
    if (!isChecked) return;
    if (isBotListFetching) return;
    if (Array.isArray(BotNullifyList) && BotNullifyList.length === 0) {
      setIsChecked(false);
      setIsBotListLoading(false);
    }
  }, [shouldFetchBotNullifyList, isBotListFetching, BotNullifyList, isChecked]);

  const handleToggle = () => {
    const newCheckedState = !isChecked;
    setIsChecked(newCheckedState);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsChecked(false);
    setIsBotListLoading(false);
  };

  const handleConfirm = () => {
    setIsModalOpen(false);
    setIsChecked(false);
    if (errorUrl) {
      window.open(errorUrl, '_blank');
    }
  };

  useEffect(() => {
    if (searchTerm?.length === 0) {
      setDebouncedSearchTerm('');
      setShowPopup(false);
      return;
    }
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 800);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm?.length > 0 && (searchTerm === debouncedSearchTerm)) {
      refetch();
    }
  }, [searchTerm, debouncedSearchTerm]); 

  useEffect(() => {
    if (customAudiences?.data?.length > 0) {
      setShowPopup(true);
      setCustomAudienceOptions(customAudiences?.data);
    } else {
      setShowPopup(false);
    }
  }, [customAudiences]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAudienceSelect = (audience) => {
    setselectedCustomAudiences(prevAudiences => {
      const filteredAudiences = filterSelectedCustomAudiences(audience, prevAudiences);
      const updatedAudiences = [...filteredAudiences, audience];
      setTimeout(() => onIncludeAudienceChange(updatedAudiences), 0);
      return updatedAudiences;
    });
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setShowPopup(false);
  };

  const removeAudienceEntry = (audience_id) => {
    setselectedCustomAudiences(prevAudiences => {
      const updatedAudiences = prevAudiences.filter(audience => audience.audience_id !== audience_id);
      setTimeout(() => onIncludeAudienceChange(updatedAudiences), 0);
      return updatedAudiences;
    });
  };

  // While modal is open, if BotNullifyList becomes available, merge-add them to the selection.
  useEffect(() => {
    if (isModalOpen && BotNullifyList?.length > 0) {
      const botAudiences = BotNullifyList.map(item => ({
        audience_id: item.botId,
        name: item.botName,
      }));

      setselectedCustomAudiences(prev => {
        const updated = [
          ...prev.filter(a => !botAudiences.some(b => b.audience_id === a.audience_id)),
          ...botAudiences,
        ];
        setTimeout(() => onIncludeAudienceChange(updated), 0);
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, BotNullifyList]);

  const showSkeleton = (isChecked && shouldFetchBotNullifyList && (isBotListFetching || !Array.isArray(BotNullifyList)) || isBotListLoading);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={1}>
      <Flex width={'100%'}>
        {matchedPixel?.pixel_id ? (
          <Flex alignItems="center" gap={2}>
            <Text fontWeight="bold" fontSize="14px">Bot Nullifier</Text>
            <Tooltip
              borderRadius={5}
              label={
                <Box fontSize="12px" p={1}>
                  <Text>
                    {isSwitchDisabled ? 'Select pixel before enabling bot nullifier' : 'A safeguard that reduces the effect of bot traffic by filtering it out and keeping only the top 95% of real, high-quality users.'}
                  </Text>
                </Box>
              }
              fontSize="md"
            >
              <InfoOutlineIcon pointerEvents="all" />
            </Tooltip>
            <Switch
              isChecked={isChecked}
              onChange={handleToggle}
              colorScheme="brand"
              size="sm"
              isDisabled={isSwitchDisabled}
            />
          </Flex>
        ) : null}
      </Flex>

      <Box>
        {showSkeleton ? (
          <Flex width="100%">
            <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
          </Flex>
        ) : (
          selectedCustomAudiences?.map((audience) => (
            <Flex key={audience?.audience_id} bg={inputBackground} align={'center'} justify={'space-between'} width={'full'} py={'5px'} px={'10px'} borderRadius={'md'} mb={'5px'}>
              <Flex flex={1} pr={'10px'} align={'center'}>
                <Flex justify={'space-between'} flex={1} align={'center'}>
                  <Text ml={'5px'} fontSize={'14px'} fontWeight={'bold'}>{audience?.name}</Text>
                </Flex>
              </Flex>
              <CloseIcon _hover={{ cursor: 'pointer', color: 'red.400' }} boxSize={3} color='gray.500' onClick={() => removeAudienceEntry(audience?.audience_id)} />
            </Flex>
          ))
        )}
      </Box>

      <Box position="relative">
        <Flex>
          <InputGroup>
            <InputLeftElement height={'32px'}>
              <SearchIcon boxSize={4} color='gray.500' />
            </InputLeftElement>
            <Input
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search existing audiences"
              autoComplete="off"
              borderRadius={'md'}
              size={'sm'}
              onFocus={() => {
                setIsInputFocused(true);
                if (customAudienceOptions?.length > 0) setShowPopup(true);
              }}
              onBlur={() => {
                setTimeout(() => setIsInputFocused(false), 100);
              }}
            />
            <InputRightElement height={'32px'}>
              {isFetching ? (
                <Spinner boxSize={3} />
              ) : (
                <>
                  {searchTerm?.length > 0 ? (
                    <CloseIcon boxSize={3} color='gray.500' onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }} />
                  ) : null}
                </>
              )}
            </InputRightElement>
          </InputGroup>
        </Flex>

        {showPopup && customAudienceOptions.length > 0 && (
          <List
            ref={popupRef}
            position="absolute"
            mt={2}
            top="100%"
            width="100%"
            border="1px solid"
            borderColor={borderColor}
            backgroundColor={inputBackground}
            zIndex="1000"
            maxHeight="200px"
            overflowY="auto"
          >
            {customAudienceOptions.map((audience) => (
              <ListItem
                key={audience.audience_id * Math.random() + audience.name}
                p={'5px'}
                px={'10px'}
                cursor="pointer"
                _hover={{ backgroundColor: hoverColor }}
                onClick={() => handleAudienceSelect(audience)}
              >
                <Flex justify={'space-between'}>
                  <Text fontSize={'14px'}>{audience.name}</Text>
                  <Text fontSize={'12px'}>{audience.parent_category}</Text>
                </Flex>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Modal for when TOS is not accepted and no previous list exists */}
      <Modal isOpen={isModalOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Action</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            You need to accept the terms of service for custom audiences before proceeding. Would you like to do that now?
          </ModalBody>
        <ModalFooter>
            <Button variant={'solid'} colorScheme={'gray'} size={'sm'} mr={3} onClick={handleModalClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" size={'sm'} onClick={handleConfirm}>
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default IncludeCustomAudienceWithBotNullifierUpdate;
